// Port of the design's view model (Claude Design project "rebounding dashboard", rebounding-model.js): bands,
// windows, sort order and team rows. Keep it in step with the design rather than improving it here.
//
// Differences from the design file, all data plumbing:
// - "Today" is a session id per page (meta.crashing.today / meta.boxouts.today), not one date for both, because
//   the Crashing page's latest session can be a game while Box-outs is practice only.
// - No per-player `hide`: HJ stays on the board (brief, 2026-10-01).
// - Hidden players (Players tab) are left out, rows and team row alike, unless `showHidden` (upload groups only).
// - Photos come from public/images/rebounding/headshots/<nbaId>.png (not public/rebounding/: a folder there shadows the /rebounding route); a missing file falls back to initials in the page.
// - Both pages are sectioned by position group (BIGS / WINGS / GUARDS, meta.positionGroups; Crashing Trevor 2026-10-09,
//   Box-outs 2026-10-19); players with no position go last under UNASSIGNED. Crashing's groups have their own bands
//   (thresholds.crash_rate_by_group; unassigned players and the team row take the fallback); every Box-outs group
//   takes the one box-out band.
// - The primary column is the viewer's pick (Trevor 2026-10-19): latest, Last 10 or Season. It comes first, has the
//   full bar and the counts, and sorts the board; on the box-out % view its window's DRB and OREB allowed follow it
//   as plain totals.
// - Box-outs shows the score or box-out % (Trevor 2026-10-19, for the video coordinator): the % pools box-outs over
//   chances like crash rate pools crashes, with its own band (thresholds.boxout_pct) and a pooled team row.
// - A box-out session with nothing on it (no chances, DREB or OREB allowed) is no score, not 0 (Trevor 2026-10-19):
//   it shows "—" in the no-data grey and is left out of the windows' averages and the team average.
// - The latest column is named for its session (meta.<page>.todayEvent: "10/16 @ GSW"), not "Today".
import {
  BoxoutSession,
  CrashSession,
  ReboundingDashboard,
  ReboundingPage,
  ReboundingPlayer,
} from "./types";

export const ICON = {
  good: "M4.5 12.5L10 18L19.5 6.5",
  mid: "M5 12H19",
  bad: "M6.5 6.5L17.5 17.5M17.5 6.5L6.5 17.5",
};

export type Ground = "Black" | "White";
export type BoxoutMetric = "score" | "pct";
// The three windows: the latest session, Last 10, Season.
export type Window = "t" | "l" | "s";
export const WINDOWS: Window[] = ["t", "l", "s"];

// Before the API sends thresholds.boxout_pct (config.py, 2026-10-19).
const BOXOUT_PCT_BAND = { green: 0.8, red: 0.7 };

export interface Palette {
  bg: string;
  fg: string;
  sub: string;
  mute: string;
  rule: string;
  headRule: string;
  photoBg: string;
  photoInk: string;
  logo: string;
  pillBorder: string;
  off: string;
  zebra: string;
  good: string;
  mid: string;
  bad: string;
  none: string;
  noneInk: string;
  absentBorder: string;
  absentInk: string;
}

const ASSETS = `${process.env.PUBLIC_URL ?? ""}/images/rebounding`;

export const PAL: Record<Ground, Palette> = {
  Black: {
    bg: "#000", fg: "#fff", sub: "#BDBDBD", mute: "#8A8A8A", rule: "#262626", headRule: "#fff",
    photoBg: "#1A1A1A", photoInk: "#5A5A5A", logo: `${ASSETS}/tb_primary_rgb_white.png`, pillBorder: "#5A5A5A",
    off: "#5A5A5A", zebra: "#141414", good: "#3CC170", mid: "#E3A23B", bad: "#FF3B2F", none: "#2A2A2A",
    noneInk: "#9A9A9A", absentBorder: "1px dashed #3A3A3A", absentInk: "#5A5A5A",
  },
  White: {
    bg: "#F9F9F9", fg: "#000", sub: "#3A3A3A", mute: "#5A5A5A", rule: "#E0E0E0", headRule: "#000",
    photoBg: "#EFEFEF", photoInk: "#8A8A8A", logo: `${ASSETS}/tb_primary_rgb.png`, pillBorder: "#BDBDBD",
    off: "#8A8A8A", zebra: "#EEEEEE", good: "#1F9D55", mid: "#D58C2E", bad: "#F0261B", none: "#E3E3E3",
    noneInk: "#5A5A5A", absentBorder: "1px dashed #BDBDBD", absentInk: "#8A8A8A",
  },
};

export const headshotUrl = (nbaId: string) => `${ASSETS}/headshots/${nbaId}.png`;

const fmtScore = (v: number) => {
  const r = Math.round(v * 100) / 100;
  return (r < 0 ? "−" : "") + String(Math.abs(r));
};

interface Win {
  v: number | null;
  c?: number;
  op?: number;
  tie: number;
  // box-outs: the window's DREB and OREB allowed
  dreb?: number;
  oreb?: number;
}

export interface Cell {
  val: string;
  count?: string;
  fill: string;
  border?: string;
  icon: string;
  ink: string;
}

export interface BoardRow {
  key: string;
  // the section heading, on the first row of each Crashing section
  heading?: string;
  first: string;
  last: string;
  nbaId: string | null;
  initials: string;
  zebra: string;
  hidden: boolean;
  // the primary window's cell is the big one (val + count); the others are small
  t: Cell;
  l: Cell;
  s: Cell;
  // box-out % view: the primary window's DRB and OREB allowed, plain
  detail?: { dreb: string; oreb: string };
}

export interface Board {
  P: Palette;
  crash: boolean;
  rows: BoardRow[];
  team: { t: Cell; l: Cell; s: Cell };
  primary: Window;
  // the box-out % view carries the DRB / OREB allowed columns
  detail: boolean;
  // each window's column heading
  columns: Record<Window, string>;
  title: string;
  eyebrow: string;
  sub: string;
  teamLabel: string;
  todayLabel: string;
  // the latest column's heading: "10/16 @ GSW", "10/5 PRACTICE"
  todayColumn: string;
}

type Band = "good" | "mid" | "bad" | "na";

export function build(
  d: ReboundingDashboard,
  page: ReboundingPage,
  ground: Ground,
  showHidden = false,
  boxoutMetric: BoxoutMetric = "score",
  primary: Window = "t",
): Board {
  const P = PAL[ground] || PAL.Black;
  const crash = page !== "boxouts";
  const pct = !crash && boxoutMetric === "pct";
  // crash rate and box-out % pool counts and show them; the box-out score averages per-session scores
  const pooled = crash || pct;
  const th = d.meta.thresholds;
  const today = d.meta[page].today;
  const t0 = crash ? th.crash_rate : pct ? th.boxout_pct ?? BOXOUT_PCT_BAND : th.boxout_score;
  const F = d.meta.boxout_score;
  const scoreOf = (s: BoxoutSession) =>
    F.per_box_out * s.bo + F.per_missed * (s.att - s.bo) + F.per_dreb * s.dreb + F.per_oreb_allowed * s.oreb;
  const band = (v: number | null, t = t0): Band =>
    v == null ? "na" : v >= t.green ? "good" : v < t.red ? "bad" : "mid";

  const win = (p: ReboundingPlayer, which: "today" | "last10" | "season"): Win | null => {
    if (crash) {
      let ss: CrashSession[] = p.crashing;
      if (which === "today") ss = ss.filter((s) => s.session === today);
      else if (which === "last10") ss = ss.slice(-10);
      if (!ss.length) return null;
      const c = ss.reduce((a, s) => a + s.c, 0);
      const op = ss.reduce((a, s) => a + s.o, 0);
      return { v: op ? c / op : null, c, op, tie: op };
    }
    let ss: BoxoutSession[] = p.boxouts;
    if (which === "today") ss = ss.filter((s) => s.session === today);
    else if (which === "last10") ss = ss.slice(-10);
    if (!ss.length) return null;
    const bo = ss.reduce((a, s) => a + s.bo, 0);
    const att = ss.reduce((a, s) => a + s.att, 0);
    const dreb = ss.reduce((a, s) => a + s.dreb, 0);
    const oreb = ss.reduce((a, s) => a + s.oreb, 0);
    if (pct) return { v: att ? bo / att : null, c: bo, op: att, tie: att, dreb, oreb };
    const sc = ss.filter((s) => s.att || s.dreb || s.oreb).map(scoreOf);
    return { v: sc.length ? sc.reduce((a, b) => a + b, 0) / sc.length : null, tie: att ? bo / att : 0 };
  };
  const fmt = (x: Win) => (pooled ? Math.round((x.v ?? 0) * 100) + "%" : fmtScore(x.v ?? 0));
  const fill = (b: Band) => (b === "na" ? P.none : P[b]);
  const big = (x: Win | null, t = t0): Cell => {
    if (!x) return { val: "—", count: "", fill: "transparent", border: P.absentBorder, icon: "", ink: P.absentInk };
    const b = band(x.v, t);
    return {
      val: x.v == null ? "—" : fmt(x),
      count: pooled ? x.c + "/" + x.op : "",
      fill: fill(b),
      border: "none",
      icon: b === "na" ? "" : ICON[b],
      ink: b === "na" ? P.noneInk : "#000",
    };
  };
  const small = (x: Win | null, t = t0): Cell => {
    if (!x) return { val: "—", fill: "transparent", icon: "", ink: P.absentInk };
    const b = band(x.v, t);
    return { val: x.v == null ? "—" : fmt(x), fill: fill(b), icon: b === "na" ? "" : ICON[b], ink: P.fg };
  };

  const players = d.players
    .filter((p) => showHidden || !p.hidden)
    .map((p) => ({ p, t: win(p, "today"), l: win(p, "last10"), s: win(p, "season") }))
    .filter((r) => r.l);
  type PRow = (typeof players)[number];
  // The primary window sorts; a player without it (only the latest session can be missing) goes after, by Last 10.
  const ordered = (rs: PRow[]) => [
    // no value sorts last (a box-out score can be negative)
    ...rs
      .filter((r) => r[primary])
      .sort((a, b) => (b[primary]!.v ?? -Infinity) - (a[primary]!.v ?? -Infinity) || b[primary]!.tie - a[primary]!.tie),
    ...rs.filter((r) => !r[primary]).sort((a, b) => (b.l!.v ?? -Infinity) - (a.l!.v ?? -Infinity)),
  ];
  // One section per position group: Crashing's on their own bands, Box-outs' all on the box-out band.
  const groups = d.meta.positionGroups ?? [];
  const known = new Set(groups.map((g) => g.key));
  const sections: { label?: string; band: typeof t0; rows: PRow[] }[] = groups.length
    ? [
        ...groups.map((g) => ({
          label: g.label,
          band: crash ? th.crash_rate_by_group?.[g.key] ?? t0 : t0,
          rows: players.filter((r) => r.p.positionGroup === g.key),
        })),
        { label: "UNASSIGNED", band: t0, rows: players.filter((r) => !known.has(r.p.positionGroup ?? "")) },
      ].filter((sec) => sec.rows.length)
    : [{ band: t0, rows: players }];
  const cell = (w: Window, x: Win | null, t = t0) => (w === primary ? big(x, t) : small(x, t));
  const count = (n: number | undefined) => (n == null ? "—" : String(n));
  const rows: BoardRow[] = sections.flatMap((sec) =>
    ordered(sec.rows).map((r, i) => ({
      key: r.p.key,
      heading: i === 0 ? sec.label : undefined,
      first: r.p.first,
      last: r.p.last || "",
      nbaId: r.p.nbaId,
      initials: r.p.first.slice(0, 2),
      zebra: i % 2 ? P.zebra : "transparent",
      hidden: !!r.p.hidden,
      t: cell("t", r.t, sec.band),
      l: cell("l", r.l, sec.band),
      s: cell("s", r.s, sec.band),
      detail: pct ? { dreb: count(r[primary]?.dreb), oreb: count(r[primary]?.oreb) } : undefined,
    })),
  );

  type Key = "t" | "l" | "s";
  let team: Board["team"];
  if (pooled) {
    // Pooled from the players' counts (crash rate: the engine's unscoped team CrashRateAdj reads high since CTG 0.8.0).
    const agg = (which: Key): Win | null => {
      let c = 0;
      let op = 0;
      players.forEach((r) => {
        const x = r[which];
        if (x) {
          c += x.c!;
          op += x.op!;
        }
      });
      return op ? { v: c / op, c, op, tie: op } : null;
    };
    team = { t: cell("t", agg("t")), l: cell("l", agg("l")), s: cell("s", agg("s")) };
  } else {
    const avg = (which: Key): Win | null => {
      const xs = players.map((r) => r[which]).filter((x): x is Win => x?.v != null);
      return xs.length ? { v: xs.reduce((a, x) => a + x.v!, 0) / xs.length, tie: 0 } : null;
    };
    team = { t: cell("t", avg("t")), l: cell("l", avg("l")), s: cell("s", avg("s")) };
  }
  const todayColumn = (d.meta[page].todayEvent || d.meta[page].todayLabel || "Latest").toUpperCase();

  return {
    P,
    crash,
    rows,
    team,
    primary,
    detail: pct,
    columns: { t: todayColumn, l: "LAST 10", s: "SEASON" },
    title: crash ? "CRASHING" : "BOX-OUTS",
    eyebrow: crash ? "OFFENSE · REBOUNDING" : "DEFENSE · REBOUNDING",
    sub: crash
      ? "CRASHES ON TEAMMATES' THREES"
      : pct
        ? "BOX-OUTS ÷ BOX-OUT CHANCES"
        : "+0.5 BOX-OUT · −0.25 MISSED · +1 DREB · −0.5 OREB ALLOWED",
    teamLabel: pooled ? "TEAM" : "TEAM AVG",
    todayLabel: (d.meta[page].todayEvent || d.meta[page].todayLabel || "No sessions yet").toUpperCase(),
    todayColumn,
  };
}
